import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { getSupabase } from "@/integrations/supabase/lazy";

// Toast-Bibliothek erst bei Bedarf laden
const toast = {
  error: async (m: string) => (await import("sonner")).toast.error(m),
  success: async (m: string) => (await import("sonner")).toast.success(m),
};

const newsletterLists = [
  { id: 6, label: "Events" },
  { id: 5, label: "Kurse" },
  { id: 3, label: "Pressekontakt" },
  { id: 10, label: "Kinderkurse" },
  { id: 12, label: "Gesundheitssport" },
  { id: 9, label: "Yoga" },
  { id: 11, label: "QiGong" },
];

export function NewsletterForm() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedLists, setSelectedLists] = useState<number[]>([6]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const toggleList = (listId: number) => {
    setSelectedLists((prev) =>
      prev.includes(listId) ? prev.filter((id) => id !== listId) : [...prev, listId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || selectedLists.length === 0) {
      if (selectedLists.length === 0) {
        toast.error("Bitte wähle mindestens ein Thema aus.");
      }
      return;
    }

    setLoading(true);
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke("subscribe-newsletter", {
        body: { email: email.trim(), firstName: firstName.trim(), listIds: selectedLists },
      });

      if (error) throw error;

      if (data?.success) {
        setSuccess(true);
        setEmail("");
        setFirstName("");
        toast.success(data.message || "Erfolgreich angemeldet!");
        setTimeout(() => setSuccess(false), 5000);
      } else {
        toast.error(data?.error || "Anmeldung fehlgeschlagen.");
      }
    } catch {
      toast.error("Ein Fehler ist aufgetreten. Bitte versuche es später erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h4 className="font-display font-semibold text-foreground mb-2 flex items-center gap-2">
        <Mail size={18} className="text-primary" />
        Newsletter
      </h4>
      <p className="text-muted-foreground mb-3">
        Wähle deine Themen und bleib auf dem Laufenden.
      </p>
      {success ? (
      <div className="flex items-center gap-2 text-sm text-primary">
          <CheckCircle size={16} />
          <span>Fast geschafft! Bitte bestätige deine Anmeldung über den Link in der E-Mail.</span>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="space-y-3"
          name="newsletter-signup"
          data-webmcp-tool="newsletter_signup"
          {...{ toolname: "newsletter_signup" }}
          data-webmcp-description="Newsletter der Kulturmanufaktur abonnieren (Double-Opt-in). Erfordert Vorname, E-Mail, mindestens eine Themenliste und Zustimmung zum Datenschutz."
          {...{ tooldescription: "Newsletter der Kulturmanufaktur abonnieren (Double-Opt-in). Erfordert Vorname, E-Mail, mindestens eine Themenliste und Zustimmung zum Datenschutz." }}
        >
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {newsletterLists.map((list) => (
              <label
                key={list.id}
                className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
              >
                <Checkbox
                  checked={selectedLists.includes(list.id)}
                  onCheckedChange={() => toggleList(list.id)}
                  disabled={loading}
                  className="h-3.5 w-3.5"
                />
                {list.label}
              </label>
            ))}
          </div>
          <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer">
            <Checkbox
              checked={privacyAccepted}
              onCheckedChange={(checked) => setPrivacyAccepted(checked === true)}
              disabled={loading}
              className="h-3.5 w-3.5 mt-0.5 shrink-0"
            />
            <span>
              Ich habe die{" "}
              <Link to="/datenschutz" className="text-primary underline hover:text-primary/80">
                Datenschutzbestimmungen
              </Link>{" "}
              gelesen und stimme der Verarbeitung meiner Daten zum Newsletterversand zu. Abmeldung jederzeit möglich.
            </span>
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="text"
              name="firstName"
              autoComplete="given-name"
              aria-label="Vorname"
              data-webmcp-field="firstName"
              data-webmcp-description="Vorname der Person, die den Newsletter abonnieren möchte"
              {...{ toolparamdescription: "Vorname der Person, die den Newsletter abonnieren möchte" }}
              data-webmcp-required="true"
              placeholder="Dein Vorname"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              disabled={loading}
              className="sm:w-1/3 h-10 text-base"
            />
            <Input
              type="email"
              name="email"
              autoComplete="email"
              aria-label="E-Mail-Adresse"
              data-webmcp-field="email"
              data-webmcp-description="E-Mail-Adresse für den Newsletter (Double-Opt-in)"
              {...{ toolparamdescription: "E-Mail-Adresse für den Newsletter (Double-Opt-in)" }}
              data-webmcp-required="true"
              placeholder="Deine E-Mail-Adresse"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className="flex-1 h-10 text-base"
            />

            <Button type="submit" variant="hero" size="sm" disabled={loading || selectedLists.length === 0 || !privacyAccepted} className="shrink-0 !bg-primary !text-primary-foreground hover:!bg-primary/80 disabled:!opacity-70 disabled:!bg-primary/60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              <span className="sr-only sm:not-sr-only">Eintragen</span>
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
